import React, { useEffect, useMemo, useState } from 'react';
import { endOfDay, format, isValid, parseISO, startOfDay } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const WeeklyStatementModal = ({ isOpen, onClose, startDate, endDate, dateBasis }) => {
  const { currentOrgId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [expandedVendors, setExpandedVendors] = useState({});

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !currentOrgId || !startDate || !endDate) return;
      setLoading(true);
      try {
        let query = supabase
          .from('parsed_invoices')
          .select('id, vendor_name, invoice_number, invoice_date, created_at, net_amount, vat_amount, gross_amount, currency, status')
          .eq('org_id', currentOrgId)
          .in('status', ['reviewed', 'exported']);

        if (dateBasis === 'created_at') {
          query = query
            .gte('created_at', startOfDay(startDate).toISOString())
            .lte('created_at', endOfDay(endDate).toISOString());
        } else {
          query = query
            .gte('invoice_date', format(startDate, 'yyyy-MM-dd'))
            .lte('invoice_date', format(endDate, 'yyyy-MM-dd'));
        }

        const { data, error } = await query;
        if (error) throw error;
        setInvoices(data || []);
      } catch (err) {
        console.error('Failed to load weekly statement:', err);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, currentOrgId, startDate, endDate, dateBasis]);

  const grouped = useMemo(() => {
    const map = {};
    for (const inv of invoices) {
      const vendor = inv?.vendor_name || 'Unknown Vendor';
      if (!map[vendor]) {
        map[vendor] = {
          vendor,
          currency: inv?.currency || 'GBP',
          invoices: [],
          net: 0,
          vat: 0,
          gross: 0
        };
      }
      map[vendor].invoices.push(inv);
      map[vendor].net += toNum(inv?.net_amount) || 0;
      map[vendor].vat += toNum(inv?.vat_amount) || 0;
      map[vendor].gross += toNum(inv?.gross_amount) || 0;
    }

    return Object.values(map).sort((a, b) => b.gross - a.gross);
  }, [invoices]);

  const grandTotals = useMemo(() => {
    return grouped.reduce(
      (acc, group) => ({
        net: acc.net + group.net,
        vat: acc.vat + group.vat,
        gross: acc.gross + group.gross
      }),
      { net: 0, vat: 0, gross: 0 }
    );
  }, [grouped]);

  const formatCurrency = (value, currency = 'GBP') => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return '£0.00';
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(n);
    } catch {
      return `£${n.toFixed(2)}`;
    }
  };

  const rangeLabel = startDate && endDate
    ? `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`
    : '';

  const title = dateBasis === 'created_at'
    ? 'Weekly Batch Report (Uploads)'
    : 'Weekly Purchase Statement';

  const toggleVendor = (vendor) => {
    setExpandedVendors((prev) => ({ ...prev, [vendor]: !prev[vendor] }));
  };

  const downloadCsv = () => {
    const headers = ['Vendor', 'Invoice Date', 'Upload Date', 'Invoice Ref', 'Net', 'VAT', 'Gross', 'Status'];
    const rows = grouped.flatMap((group) =>
      group.invoices.map((inv) => ({
        vendor: inv.vendor_name || group.vendor || 'Unknown',
        invoiceDateRaw: inv.invoice_date || '',
        invoiceDate: formatCsvDate(inv.invoice_date, false),
        uploadDate: formatCsvDate(inv.created_at, true),
        ref: inv.invoice_number || '',
        net: toNum(inv.net_amount) || 0,
        vat: toNum(inv.vat_amount) || 0,
        gross: toNum(inv.gross_amount) || 0,
        status: inv.status || ''
      }))
    );

    rows.sort((a, b) => {
      const vendorCmp = a.vendor.localeCompare(b.vendor);
      if (vendorCmp !== 0) return vendorCmp;
      return String(a.invoiceDateRaw || '').localeCompare(String(b.invoiceDateRaw || ''));
    });

    const csvRows = [headers.join(',')];
    rows.forEach((row) => {
      const line = [
        escapeCsv(row.vendor),
        escapeCsv(row.invoiceDate),
        escapeCsv(row.uploadDate),
        escapeCsv(row.ref),
        row.net.toFixed(2),
        row.vat.toFixed(2),
        row.gross.toFixed(2),
        escapeCsv(row.status)
      ];
      csvRows.push(line.join(','));
    });

    csvRows.push(',,,,,,,');
    csvRows.push(
      [
        escapeCsv('GRAND TOTAL'),
        '',
        '',
        '',
        grandTotals.net.toFixed(2),
        grandTotals.vat.toFixed(2),
        grandTotals.gross.toFixed(2),
        ''
      ].join(',')
    );

    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `weekly_statement_${format(startDate, 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Icon name="FileText" size={18} className="text-primary" />
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            </div>
            <p className="text-sm text-muted-foreground">{rangeLabel}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading weekly report…</div>
          ) : grouped.length === 0 ? (
            <div className="text-sm text-muted-foreground bg-muted/40 border border-dashed border-border rounded-lg p-6 text-center">
              No reviewed or exported invoices found for this week.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-5 text-xs font-semibold text-muted-foreground border-b border-border pb-2">
                <div>Vendor</div>
                <div className="text-right">Inv Count</div>
                <div className="text-right">Net</div>
                <div className="text-right">VAT</div>
                <div className="text-right">Gross</div>
              </div>

              {grouped.map((group) => (
                <div key={group.vendor} className="border border-border rounded-lg">
                  <button
                    className="w-full grid grid-cols-5 items-center gap-2 p-3 text-sm text-foreground hover:bg-muted/40 transition-smooth"
                    onClick={() => toggleVendor(group.vendor)}
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        name={expandedVendors[group.vendor] ? 'ChevronDown' : 'ChevronRight'}
                        size={14}
                        className="text-muted-foreground"
                      />
                      <span className="font-medium">{group.vendor}</span>
                    </div>
                    <div className="text-right">{group.invoices.length}</div>
                    <div className="text-right">{formatCurrency(group.net, group.currency)}</div>
                    <div className="text-right">{formatCurrency(group.vat, group.currency)}</div>
                    <div className="text-right">{formatCurrency(group.gross, group.currency)}</div>
                  </button>

                  {expandedVendors[group.vendor] && (
                    <div className="border-t border-border bg-background/60">
                      <div className="grid grid-cols-4 text-[11px] font-semibold text-muted-foreground px-4 py-2">
                        <div>Date</div>
                        <div>Reference</div>
                        <div className="text-right">Net</div>
                        <div className="text-right">Gross</div>
                      </div>
                      {group.invoices.map((inv) => (
                        <div key={inv.id} className="grid grid-cols-4 px-4 py-2 text-xs text-foreground border-t border-border/60">
                          <div>{formatInvoiceDate(inv, dateBasis)}</div>
                          <div>{inv.invoice_number || '—'}</div>
                          <div className="text-right">{formatCurrency(inv.net_amount, inv.currency)}</div>
                          <div className="text-right">{formatCurrency(inv.gross_amount, inv.currency)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-border flex items-center justify-between">
          <div className="text-sm font-medium text-foreground">
            Grand Total: {formatCurrency(grandTotals.gross, grouped?.[0]?.currency || 'GBP')}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadCsv} iconName="Download" iconPosition="left" disabled={!grouped.length}>
              Download CSV
            </Button>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const toNum = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatInvoiceDate = (inv, dateBasis) => {
  const raw = dateBasis === 'created_at' ? inv?.created_at : inv?.invoice_date;
  if (!raw) return '—';
  const parsed = parseISO(raw);
  if (!isValid(parsed)) return '—';
  return format(parsed, 'dd/MM/yyyy');
};

const escapeCsv = (value) => {
  const str = String(value ?? '');
  if (/[\",\\n]/.test(str)) {
    return `\"${str.replace(/\"/g, '\"\"')}\"`;
  }
  return str;
};

const formatCsvDate = (value, includeTime = false) => {
  if (!value) return '';
  const parsed = parseISO(value);
  if (!isValid(parsed)) return '';
  return includeTime ? format(parsed, 'dd/MM/yyyy HH:mm') : format(parsed, 'dd/MM/yyyy');
};

export default WeeklyStatementModal;
